/**
 * AppShell — layout платформенной зоны по макету (design_handoff): светлый
 * сайдбар (секция «Платформа» + nav из DOMAINS + блок команды с выходом
 * внизу) и область контента. Топ-бар — на самой странице.
 *
 * На мобилке (<md) сайдбар скрыт, навигация открывается кнопкой-гамбургером
 * через Sheet слева (временное решение — в хендоффе мобильной навигации нет).
 */

import {useState} from 'react';
import {NavLink, Outlet, useNavigate} from 'react-router-dom';
import {ChevronsUpDown, KeyRound, LogOut, Menu} from 'lucide-react';

import {logoutTeam} from '@services/auth';
import {useAuth} from '@hooks/useAuth';
import {BrandMark} from '@components/BrandMark';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@components/ui/DropdownMenu';
import {Sheet, SheetContent, SheetTrigger} from '@components/ui/Sheet';
import {cn} from '@lib/utils';
import {DOMAINS} from '@routes/domains';

import {ApplicationsBadge} from './ApplicationsBadge';

type SidebarNavProps = {
  onLogout: () => void;
  onChangePassword: () => void;
  onNavigate?: () => void;
};

const SidebarNav = ({onLogout, onChangePassword, onNavigate}: SidebarNavProps) => (
  <>
    <div className="flex items-center gap-2.5 px-2 pb-5 pt-1">
      <BrandMark size={30} />
    </div>

    <div className="px-2.5 py-1.5 text-[10.5px] font-semibold uppercase tracking-[.09em] text-muted-foreground/80">
      Платформа
    </div>
    <nav className="flex flex-col gap-0.5">
      {DOMAINS.map((section) => (
        <NavLink
          key={section.id}
          to={section.path}
          onClick={onNavigate}
          className={({isActive}) =>
            cn(
              'relative flex items-center gap-3 rounded-[9px] px-[11px] py-[9px] text-sm font-medium transition-colors [&_svg]:size-[19px]',
              isActive
                ? 'bg-accent font-semibold text-accent-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          {({isActive}) => (
            <>
              {isActive && (
                <span className="absolute -left-3.5 bottom-[9px] top-[9px] w-[3px] rounded-r-[3px] bg-primary" />
              )}
              {section.icon}
              {section.title}
              {section.id === 'applications' && <ApplicationsBadge />}
            </>
          )}
        </NavLink>
      ))}
    </nav>

    <DropdownMenu>
      <DropdownMenuTrigger className="mt-auto flex items-center gap-3 rounded-[9px] border-t border-sidebar-border px-2 pt-3.5 text-left outline-none">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground">
          SK
        </span>
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block text-[13px] font-semibold text-foreground">Команда Skrezo</span>
          <span className="block text-[11px] text-muted-foreground">Общий доступ</span>
        </span>
        <ChevronsUpDown className="size-4 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[210px]">
        <DropdownMenuItem onClick={onChangePassword}>
          <KeyRound />
          Сменить пароль
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onLogout}>
          <LogOut />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </>
);

export const AppShell = () => {
  const navigate = useNavigate();
  const {logout} = useAuth();
  const [navOpen, setNavOpen] = useState(false);

  const handleLogout = () => {
    // Сначала гасим refresh на сервере (читает его до локальной зачистки)
    void logoutTeam();
    logout();
    navigate('/login', {replace: true});
  };
  const handleChangePassword = () => {
    setNavOpen(false);
    navigate('/change-password');
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Десктоп-сайдбар */}
      <aside className="sticky top-0 hidden h-screen w-[246px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar px-3.5 py-5 md:flex">
        <SidebarNav onLogout={handleLogout} onChangePassword={handleChangePassword} />
      </aside>

      {/* Мобильная навигация: гамбургер + Sheet слева */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetTrigger
          className="fixed right-4 top-3 z-40 grid size-10 place-items-center rounded-xl border border-input bg-card md:hidden"
          aria-label="Меню"
        >
          <Menu className="size-5 text-[#3A3D45]" />
        </SheetTrigger>
        <SheetContent side="left" className="flex w-[246px] flex-col bg-sidebar px-3.5 py-5">
          <SidebarNav
            onLogout={handleLogout}
            onChangePassword={handleChangePassword}
            onNavigate={() => setNavOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <main className="min-w-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};
